import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useState } from 'react';
import { Autocomplete, Box, DialogContent, FormControl, TextField } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';
import { getEMerchant, getFeeClasses, getMonthlyFees } from 'redux/reducers/monthlyFee/selectors';
import { addMonthlyFeeRequest } from 'redux/reducers/monthlyFee/reducer';
import { TCreateMonthlyFeeChangeRequestDialogProps } from './types';

const monthlyFeeEasFee = [
	{ monthlyFee: 4, easFee: 205 },
	{ monthlyFee: 7, easFee: 205 },
	{ monthlyFee: 10, easFee: 205 },
	{ monthlyFee: 5, easFee: 206 },
	{ monthlyFee: 8, easFee: 206 },
	{ monthlyFee: 11, easFee: 206 },
	{ monthlyFee: 6, easFee: 207 },
	{ monthlyFee: 9, easFee: 207 },
	{ monthlyFee: 12, easFee: 207 },
	{ monthlyFee: 14, easFee: 205 },
	{ monthlyFee: 15, easFee: 206 },
	{ monthlyFee: 17, easFee: 207 },
	{ monthlyFee: 18, easFee: 205 },
	{ monthlyFee: 20, easFee: 206 },
	{ monthlyFee: 21, easFee: 207 },
];

export const CreateMonthlyFeeChangeRequestDialog: FC<TCreateMonthlyFeeChangeRequestDialogProps> = ({
	open,
	setClose,
}) => {
	const dispatch = useDispatch();
	const monthlyFeeList = useSelector(getMonthlyFees);
	const monthlyFeeDescriptions = monthlyFeeList?.map(item => item.description) ?? [];
	const [monthlyFee, setMonthlyFee] = useState<string | null>(null);
	const [inputMonthlyFee, setInputMonthlyFee] = useState('');

	const easFeeList = useSelector(getFeeClasses);
	const easFeeDescriptions = easFeeList?.map(item => item.description) ?? [];
	const [easFee, setEasFee] = useState<string | null>(null);
	const [inputEasFee, setInputEasFee] = useState('');

	const emList = useSelector(getEMerchant);
	const emListFullNames = emList?.map(item => item.fullName) ?? [];
	const [em, setEm] = useState<string | null>(null);
	const [inputEm, setInputEm] = useState('');

	const [requestedAt, setRequestedAt] = useState<DateTime | null>(DateTime.now());

	const reset = () => {
		setMonthlyFee(null);
		setInputMonthlyFee('');
		setEasFee(null);
		setInputEasFee('');
		setEm(null);
		setRequestedAt(DateTime.now());
	};

	const handleChangeMonthlyFee = (event: any, newValue: string | null) => {
		if (!newValue) return;
		setMonthlyFee(newValue);
		setInputMonthlyFee(newValue ?? '');
		if (!easFee && newValue && monthlyFeeList) {
			const monthlyFeeId = monthlyFeeList?.find(item => item.description === newValue)?.id;
			if (!monthlyFeeId) return;

			const easFeeId = monthlyFeeEasFee?.find(item => item.monthlyFee === monthlyFeeId)?.easFee;
			if (!easFeeId) return;

			const easFeeDescription = easFeeList?.find(item => item.id === easFeeId)?.description;
			if (!easFeeDescription) return;

			setEasFee(String(easFeeDescription ?? ''));
			setInputEasFee(String(easFeeDescription ?? ''));
		}
	};

	const handleChangeEasFee = (event: any, newValue: string | null) => {
		setEasFee(newValue);
		setInputEasFee(newValue ?? '');
		if (!monthlyFee && newValue && easFeeList) {
			const easFeeId = easFeeList?.find(item => item.description === newValue)?.id;
			if (!easFeeId) return;
			const monthlyFeeId = monthlyFeeEasFee?.find(item => item.easFee === easFeeId)?.monthlyFee;
			if (!monthlyFeeId) return;
			const monthlyFeeDescription = monthlyFeeList?.find(
				item => item.id === monthlyFeeId,
			)?.description;
			if (!monthlyFeeDescription) return;
			setMonthlyFee(String(monthlyFeeDescription ?? ''));
			setInputMonthlyFee(String(monthlyFeeDescription ?? ''));
		}
	};

	const handler = () => {
		setClose();
		const monthlyFeeId = monthlyFeeList?.find(item => item.description === monthlyFee)?.id;
		const easFeeId = easFeeList?.find(item => item.description === easFee)?.id;
		const emId = emList?.find(item => item.fullName === em)?.id;

		if (!monthlyFeeId || !easFeeId || !emId) {
			return;
		}
		dispatch(
			addMonthlyFeeRequest({
				emId,
				newMonthlyFeeId: monthlyFeeId,
				newFeeClassId: easFeeId,
				requestedAt: requestedAt?.toFormat('yyyy-MM-dd') ?? '',
			}),
		);
		reset();
	};

	const handleClose = () => {
		reset();
		setClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			fullWidth
		>
			<DialogTitle id="alert-dialog-title">Create Monthly Fee Change Request</DialogTitle>
			<DialogContent>
				<Box display="flex" justifyContent="center">
					<FormControl sx={{ minWidth: 120, width: '100%', overflow: 'hidden' }}>
						<Autocomplete
							value={em}
							id="monthly-fee-input"
							options={emListFullNames ?? []}
							sx={{ width: '100%', mt: 2 }}
							renderInput={params => <TextField {...params} label="EM" />}
							onChange={(event: any, newValue: string | null) => {
								setEm(newValue);
							}}
							inputValue={inputEm}
							onInputChange={(event, newInputValue) => {
								setInputEm(newInputValue);
							}}
							getOptionLabel={option => option || ''}
						/>

						<Autocomplete
							value={monthlyFee}
							id="monthly-fee-input"
							options={monthlyFeeDescriptions ?? []}
							sx={{ width: '100%', mt: 2 }}
							renderInput={params => <TextField {...params} label="Monthly fee" />}
							onChange={handleChangeMonthlyFee}
							inputValue={inputMonthlyFee}
							onInputChange={handleChangeMonthlyFee}
							getOptionLabel={option => option || ''}
						/>

						<Autocomplete
							value={easFee}
							id="monthly-fee-input"
							options={easFeeDescriptions ?? []}
							sx={{ width: '100%', mt: 2, mb: 2 }}
							renderInput={params => <TextField {...params} label="EAS fee" />}
							onChange={handleChangeEasFee}
							inputValue={inputEasFee}
							onInputChange={handleChangeEasFee}
							getOptionLabel={option => option || ''}
						/>

						<LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="fi-FI">
							<MobileDatePicker
								label="Requested at"
								value={requestedAt}
								onChange={newValue => setRequestedAt(newValue)}
								maxDate={DateTime.now()}
								minDate={DateTime.now().minus({ month: 1 })}
								format="dd-MM-yyyy"
							/>
						</LocalizationProvider>
					</FormControl>
				</Box>
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button onClick={handler} autoFocus disabled={!monthlyFee || !em || !easFee}>
					Create Monthly Fee Change Request
				</Button>
			</DialogActions>
		</Dialog>
	);
};
